import {
  PinComponent,
  PinComponentType,
  TextAnimation,
  TextPinComponent,
  PinComponentWithAnimations,
  AnimatablePinComponent,
} from '@lessonup/pins-shared';
import { Tooltip, Button, IconAnimation, styled, spacing } from '@lessonup/ui-components';
import React from 'react';
import { useEditorTranslation } from '../../../../hooks/useEditorTranslation';
import { AnimationInteractivePinMessage } from './AnimationInteractivePinMessage';
import { AnimationList } from './AnimationList';
import { AnimationListItem } from './AnimationListItem';
import { TextAnimationSettings } from './TextAnimationSettings';
import { AnimationEligibility } from './hooks/useAnimationEligibility';
import { SortableAnimationItem } from './hooks/useSortableAnimationList';

export interface AnimationsPaneContentProps {
  eligibility: AnimationEligibility;
  items: SortableAnimationItem[];
  saveNewOrder: (activeId: string, overId: string) => void;
  getAnimationLabel: (pinComponent: PinComponent) => string;
  selectedPinComponentIds: string[];
  highlightedPinComponentId: string | undefined;
  setModeSelecting: (pinComponentIds: string[]) => void;
  setHighlightedPinComponentId: (pinComponentId: string | undefined) => void;
  deleteAnimation: (pinComponentId: string, pinComponentType: PinComponentType) => void;
  addAnimations: (pinComponents: AnimatablePinComponent[]) => void;
  saveGranularity: (
    pinComponent: TextPinComponent & PinComponentWithAnimations,
    granularity: TextAnimation['granularity']
  ) => void;
}

export function AnimationsPaneContent({
  eligibility,
  items,
  saveNewOrder,
  getAnimationLabel,
  selectedPinComponentIds,
  highlightedPinComponentId,
  setModeSelecting,
  setHighlightedPinComponentId,
  deleteAnimation,
  addAnimations,
  saveGranularity,
}: AnimationsPaneContentProps) {
  const { t } = useEditorTranslation();

  if (!eligibility.eligible && eligibility.reason === 'interactive-pin')
    return (
      <TopWrapper>
        <AnimationInteractivePinMessage />
      </TopWrapper>
    );

  return (
    <div>
      <AnimationList
        sortableIds={items.map((item) => item.sortableId)}
        onReorder={saveNewOrder}
        canAddAnimation={eligibility.eligible}
      >
        {items.map((item) => {
          const pinComponent = item.pinComponent;
          return (
            <AnimationListItem
              key={item.sortableId}
              sortableId={item.sortableId}
              componentType={pinComponent.type}
              label={getAnimationLabel(pinComponent)}
              isSelected={selectedPinComponentIds.includes(item.pinComponent.id)}
              isHighlighted={highlightedPinComponentId === item.pinComponent.id}
              onPointerDown={() => setModeSelecting([item.pinComponent.id])}
              deleteAnimation={() => deleteAnimation(item.pinComponent.id, item.pinComponent.type)}
              onMouseEnter={() => setHighlightedPinComponentId(item.pinComponent.id)}
              onMouseLeave={() => setHighlightedPinComponentId(undefined)}
            >
              {pinComponent.type === 'TEXT' && (
                <TextAnimationSettings
                  id={item.pinComponent.id}
                  granularity={pinComponent.settings.animations.entry.granularity}
                  onChange={(granularity) => saveGranularity(pinComponent, granularity)}
                />
              )}
            </AnimationListItem>
          );
        })}
      </AnimationList>
      <AddButtonWrapper>
        <Tooltip
          content={
            !eligibility.eligible && eligibility.reason === 'linked-component'
              ? t('contextSidebar.panes.animations.list.animate-component.tooltip-linked-component')
              : t('contextSidebar.panes.animations.list.animate-component.tooltip')
          }
          direction="bottom"
          hide={eligibility.eligible}
        >
          <Button
            disabled={!eligibility.eligible}
            onClick={() => {
              if (eligibility.eligible) {
                addAnimations(eligibility.eligiblePinComponents);
              }
            }}
            iconStart={<IconAnimation />}
          >
            {t('contextSidebar.panes.animations.list.animate-component.label', {
              count: selectedPinComponentIds.length,
            })}
          </Button>
        </Tooltip>
      </AddButtonWrapper>
    </div>
  );
}

const TopWrapper = styled.div`
  margin: ${spacing.size12};
`;

const AddButtonWrapper = styled.div`
  padding: ${spacing.size12};
  margin-bottom: 36px;
  display: flex;
  justify-content: center;
`;
